import { useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { triggerSound } from "../../lib/soundBus";
import SoundPicker from "./SoundPicker";
import { Plus, X, Dices, Pencil, FileText, Heart, Minus } from "lucide-react";
import RollPrivacyControl from "./RollPrivacyControl";

const DICE = ["d4", "d6", "d8", "d10", "d12", "d20", "d100"];

export default function PaperRoller({ gameId, character, onCharacterUpdate, isGm, members, myMemberId }) {
  const [die, setDie] = useState("d20");
  const [bonuses, setBonuses] = useState([]);
  const [chipLabel, setChipLabel] = useState("");
  const [chipValue, setChipValue] = useState("");
  const [presetLabel, setPresetLabel] = useState("");
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [rollPrivacy, setRollPrivacy] = useState({ is_private: false, whisper_target_member_id: null });

  const patchChar = async (updates) => {
    const { data } = await api.patch(`/characters/${character.id}`, updates);
    onCharacterUpdate(data);
    return data;
  };

  const addChip = () => {
    if (!chipLabel.trim() || chipValue === "") return;
    setBonuses([...bonuses, { label: chipLabel, value: parseInt(chipValue, 10) || 0 }]);
    setChipLabel("");
    setChipValue("");
  };

  const removeChip = (i) => setBonuses(bonuses.filter((_, idx) => idx !== i));

  const roll = async (rollDie, customBonuses, customLabel) => {
    try {
      const list = customBonuses || bonuses;
      const label = customLabel || `${(rollDie || die).toUpperCase()}${list.length ? " + " + list.map((b) => b.label).join(", ") : ""}`;
      await api.post(`/characters/${character.id}/paper-roll`, {
        game_id: gameId, die: `1${rollDie || die}`, bonuses: list, label,
        is_private: rollPrivacy.is_private, whisper_target_member_id: rollPrivacy.whisper_target_member_id,
      });
      triggerSound(gameId, { tag: "dice_roll", overrideAssetId: character.paper_roll_sfx_id });
      if (rollPrivacy.whisper_target_member_id) {
        const targetName = (members || []).find((m) => m.id === rollPrivacy.whisper_target_member_id)?.display_name || "player";
        toast.success(`Rolled secretly — only you and ${targetName} can see this`);
      } else if (rollPrivacy.is_private) {
        toast.success(isGm ? "Rolled privately — only you can see this" : "Rolled privately — only you and the GM can see this");
      }
    } catch {
      toast.error("Roll failed");
    }
  };

  const setRollSfx = (assetId) => patchChar({ paper_roll_sfx_id: assetId });

  const savePreset = async () => {
    if (!presetLabel.trim() || bonuses.length === 0) return toast.error("Add bonuses and a name first");
    const existing = character.paper_presets || [];
    const newPreset = { id: editingPresetId || crypto.randomUUID(), label: presetLabel, die, bonuses };
    const presets = editingPresetId ? existing.map((p) => (p.id === editingPresetId ? newPreset : p)) : [...existing, newPreset];
    await patchChar({ paper_presets: presets });
    setPresetLabel("");
    setBonuses([]);
    setEditingPresetId(null);
    toast.success(editingPresetId ? "Quick action updated" : "Quick action saved");
  };

  const editPreset = (preset) => {
    setDie(preset.die);
    setBonuses(preset.bonuses);
    setPresetLabel(preset.label);
    setEditingPresetId(preset.id);
  };

  const cancelEdit = () => {
    setEditingPresetId(null);
    setPresetLabel("");
    setBonuses([]);
  };

  const removePreset = async (id) => {
    const presets = (character.paper_presets || []).filter((p) => p.id !== id);
    await patchChar({ paper_presets: presets });
    if (editingPresetId === id) cancelEdit();
  };

  const rollFromDropdown = (presetId) => {
    const preset = (character.paper_presets || []).find((p) => p.id === presetId);
    if (preset) roll(preset.die, preset.bonuses, preset.label);
  };

  const maxHp = character.hp_max ?? 10;
  const curHp = character.hp_current ?? maxHp;
  const adjustHp = (delta) => patchChar({ hp_current: Math.max(0, Math.min(maxHp, curHp + delta)) });

  const switchToFullSheet = async () => {
    await patchChar({ mode: "full" });
    toast.success("Switched to Full Sheet — fill in ancestry & class to auto-calculate everything");
  };

  const presets = character.paper_presets || [];

  return (
    <div className="p-6 h-full overflow-y-auto scrollbar-thin space-y-8" data-testid="paper-roller">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl mb-1">{character.name}</h2>
          <p className="text-xs uppercase tracking-widest text-[#D4AF37]">Paper Character · Quick Roller</p>
        </div>
        <button data-testid="switch-to-full-sheet-button" onClick={switchToFullSheet} className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#f1e0c5]/40 hover:text-[#D4AF37] border border-white/10 rounded-full px-3 py-1.5 shrink-0">
          <FileText className="w-3 h-3" /> Make a Full Sheet
        </button>
      </div>

      <div className="flex items-center justify-between bg-black/20 rounded-xl p-3" data-testid="paper-hp-tracker">
        <div className="flex items-center gap-2">
          <Heart className="w-4 h-4 text-[#D90429]" />
          <span className="text-xs uppercase tracking-wider text-[#f1e0c5]/50">HP</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="paper-hp-minus" onClick={() => adjustHp(-1)} className="w-7 h-7 rounded-full flex items-center justify-center bg-black/30 hover:bg-[#D90429]/20"><Minus className="w-3.5 h-3.5" /></button>
          <Input data-testid="paper-hp-current-input" type="number" value={curHp} onChange={(e) => patchChar({ hp_current: parseInt(e.target.value, 10) || 0 })} className="w-16 h-8 text-center bg-black/30 border-white/10 px-1" />
          <span className="text-[#f1e0c5]/40">/</span>
          <Input data-testid="paper-hp-max-input" type="number" value={maxHp} onChange={(e) => patchChar({ hp_max: parseInt(e.target.value, 10) || 0 })} className="w-16 h-8 text-center bg-black/30 border-white/10 px-1" />
          <button data-testid="paper-hp-plus" onClick={() => adjustHp(1)} className="w-7 h-7 rounded-full flex items-center justify-center bg-black/30 hover:bg-[#4d7c0f]/20"><Plus className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div className="bg-black/20 rounded-xl p-3">
        <SoundPicker
          gameId={gameId}
          value={character.paper_roll_sfx_id}
          onChange={setRollSfx}
          label="Roll Sound"
          testId="paper-roll-sound-picker"
        />
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-3">Quick Play — pick a labeled roll</p>
        <Select onValueChange={rollFromDropdown} disabled={presets.length === 0}>
          <SelectTrigger data-testid="quick-play-select" className="bg-black/30 border-white/10 h-12 text-base">
            <SelectValue placeholder={presets.length === 0 ? "Set up rolls below to see them here…" : "Choose a roll…"} />
          </SelectTrigger>
          <SelectContent>
            {presets.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`quick-play-option-${p.id}`}>
                {p.label} ({p.die}{p.bonuses?.length ? ` + ${p.bonuses.map((b) => b.value).join("+")}` : ""})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-3">{editingPresetId ? "Editing quick action" : "Custom roll builder"}</p>
        <div className="grid grid-cols-4 gap-3 mb-4">
          {DICE.map((d) => (
            <button
              key={d}
              data-testid={`paper-die-${d}`}
              onClick={() => setDie(d)}
              className={`h-14 rounded-xl border font-mono-sheet text-lg transition-colors duration-200 ${
                die === d ? "bg-[#D90429] border-[#D90429] text-white" : "bg-black/30 border-white/10 hover:border-[#D4AF37]/50"
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          {bonuses.map((b, i) => (
            <span key={i} data-testid={`bonus-chip-${i}`} className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-sm">
              {b.label} {b.value >= 0 ? "+" : ""}{b.value}
              <button onClick={() => removeChip(i)} data-testid={`remove-chip-${i}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 mb-4">
          <Input data-testid="chip-label-input" placeholder="e.g. Str" value={chipLabel} onChange={(e) => setChipLabel(e.target.value)} className="bg-black/30 border-white/10" />
          <Input data-testid="chip-value-input" placeholder="+4" type="number" value={chipValue} onChange={(e) => setChipValue(e.target.value)} className="bg-black/30 border-white/10 w-20" />
          <Button data-testid="add-chip-button" onClick={addChip} variant="secondary" size="icon" className="rounded-full"><Plus className="w-4 h-4" /></Button>
        </div>

        <button
          data-testid="paper-roll-button"
          onClick={() => roll()}
          className="w-full h-20 rounded-2xl bg-[#D90429] hover:bg-[#b8031f] text-white font-display text-3xl flex items-center justify-center gap-3 transition-colors duration-200 shadow-[0_0_30px_rgba(217,4,41,0.35)]"
        >
          <Dices className="w-8 h-8" /> Roll {die}
        </button>

        <div className="mt-3 flex justify-center">
          <RollPrivacyControl isGm={isGm} members={members} myMemberId={myMemberId} value={rollPrivacy} onChange={setRollPrivacy} />
        </div>

        <div className="flex gap-2 mt-4">
          <Input data-testid="preset-name-input" placeholder="Name this roll (e.g. Athletics)" value={presetLabel} onChange={(e) => setPresetLabel(e.target.value)} className="bg-black/30 border-white/10" />
          <Button data-testid="save-preset-button" onClick={savePreset} variant="secondary" className="rounded-full whitespace-nowrap">
            {editingPresetId ? "Update" : "Save as Quick Action"}
          </Button>
          {editingPresetId && (
            <Button data-testid="cancel-edit-preset-button" onClick={cancelEdit} variant="secondary" className="rounded-full">Cancel</Button>
          )}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-[#f1e0c5]/50 mb-3">Manage quick actions</p>
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <div key={p.id} className="flex items-center gap-1 bg-black/30 border border-white/10 rounded-full pl-4 pr-1.5 py-1">
              <span data-testid={`preset-${p.id}`} className="text-sm">{p.label}</span>
              <button data-testid={`edit-preset-${p.id}`} onClick={() => editPreset(p)} className="text-[#f1e0c5]/30 hover:text-[#D4AF37] p-1"><Pencil className="w-3 h-3" /></button>
              <button data-testid={`remove-preset-${p.id}`} onClick={() => removePreset(p.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429] p-1"><X className="w-3 h-3" /></button>
            </div>
          ))}
          {presets.length === 0 && <p className="text-xs text-[#f1e0c5]/30">No quick actions yet — build one above.</p>}
        </div>
      </div>
    </div>
  );
}
